import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  Loader2, CalendarDays, Trophy, Sparkles, RefreshCw, ArrowLeft, Wallet,
  Target, ListChecks, Flag, CheckCircle2, Info, Scale, GitBranch, Circle,
} from "lucide-react";
import { toast } from "sonner";

const CONF_STYLE = { "Tinggi": "bg-brand text-white", "Sedang": "bg-brand-amber text-white", "Rendah": "bg-slate-300 text-slate-700" };
const DECISION_STYLE = {
  "LANJUTKAN": "border-brand bg-brand/5 text-brand",
  "PERBAIKI": "border-brand-amber/50 bg-brand-amber/5 text-brand-amber",
  "PIVOT": "border-brand-accent/50 bg-brand-accent/5 text-brand-accent",
};

export default function Launch() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [plan, setPlan] = useState(null);
  const [hasBlueprint, setHasBlueprint] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [lRes, bRes] = await Promise.all([
          api.get("/launch").catch(() => ({ data: null })),
          api.get("/blueprint").catch(() => ({ data: null })),
        ]);
        setPlan(lRes.data || null);
        setHasBlueprint(!!bRes.data?.winner_name);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const generate = async () => {
    setGenerating(true);
    setError("");
    try {
      const { data } = await api.post("/launch/generate");
      setPlan(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Gagal membuat rencana 7 hari.");
      toast.error("Gagal membuat rencana 7 hari.");
    } finally {
      setGenerating(false);
    }
  };

  const markDay = async (day, done) => {
    try {
      const { data } = await api.patch("/launch/progress", { day, done });
      setPlan((p) => ({ ...p, completed_days: data.completed_days }));
    } catch {
      toast.error("Gagal menyimpan progres.");
    }
  };

  if (loading) return <DashboardLayout><div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-accent" /></div></DashboardLayout>;

  if (generating) {
    return (
      <DashboardLayout>
        <div className="mx-auto max-w-xl text-center py-20 animate-fade-up" data-testid="launch-generating">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-brand/10 text-brand"><Loader2 className="h-8 w-8 animate-spin" /></div>
          <h2 className="mt-6 font-heading text-xl font-bold text-ink">Menyusun rencana 7 hari Anda…</h2>
          <p className="mt-3 text-ink-soft leading-relaxed">IDEbisnis AI sedang merancang langkah harian yang kecil, terukur, dan aman untuk modal Anda.</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!plan && !hasBlueprint) {
    return (
      <DashboardLayout>
        <div className="mx-auto max-w-2xl text-center animate-fade-up" data-testid="launch-need-blueprint">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-brand/10 text-brand"><CalendarDays className="h-8 w-8" /></div>
          <h1 className="mt-6 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">Buat Blueprint Dulu</h1>
          <p className="mt-4 text-base text-ink-soft leading-relaxed">Rencana 7 Hari dibuat dari Business Blueprint Anda. Selesaikan blueprint terlebih dahulu.</p>
          <Button onClick={() => navigate("/blueprint")} data-testid="go-blueprint" className="mt-6 h-12 bg-brand hover:bg-brand-hover px-6">Ke Business Blueprint</Button>
        </div>
      </DashboardLayout>
    );
  }

  if (!plan) {
    return (
      <DashboardLayout>
        <div className="mx-auto max-w-2xl text-center animate-fade-up" data-testid="launch-generate-cta">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-brand/10 text-brand"><Sparkles className="h-8 w-8" /></div>
          <h1 className="mt-6 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">Buat Rencana 7 Hari Anda</h1>
          <p className="mt-4 text-base text-ink-soft leading-relaxed">IDEbisnis AI akan mengubah Business Winner & Blueprint Anda menjadi langkah harian kecil untuk menguji ide sebelum mengeluarkan modal besar.</p>
          {error && <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="launch-error">{error}</div>}
          <Button onClick={generate} data-testid="generate-launch-btn" className="mt-6 h-12 bg-brand hover:bg-brand-hover px-6 text-base"><Sparkles className="mr-1.5 h-5 w-5" /> Buat Rencana 7 Hari</Button>
        </div>
      </DashboardLayout>
    );
  }

  const days = plan.days || [];
  const dg = plan.decision_guide || {};
  const completedSet = new Set(plan.completed_days || []);
  const doneCount = completedSet.size;
  return (
    <DashboardLayout>
      <div className="mx-auto max-w-2xl animate-fade-up" data-testid="launch-results">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate("/blueprint")} className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-brand transition-colors" data-testid="launch-back"><ArrowLeft className="h-4 w-4" /> Blueprint</button>
          <Button variant="outline" onClick={generate} data-testid="regenerate-launch" className="border-borderc gap-1.5"><RefreshCw className="h-4 w-4" /> Buat Ulang</Button>
        </div>

        <h1 className="mt-4 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">Rencana 7 Hari Anda</h1>
        <p className="mt-3 text-base text-ink-soft leading-relaxed">Langkah kecil dan terukur untuk menguji Business Winner Anda sebelum mengeluarkan modal lebih besar.</p>

        {/* Winner reference */}
        <div className="mt-5 rounded-2xl border-2 border-brand bg-brand/5 p-5" data-testid="launch-winner-ref">
          <div className="flex items-center gap-1.5 text-brand"><Trophy className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-wide">Business Winner</span></div>
          <div className="mt-1 font-heading text-lg font-extrabold text-ink" data-testid="launch-winner-name">{plan.winner_name}</div>
          {plan.confidence && <span className={`mt-2 inline-block rounded-full px-2.5 py-0.5 text-xs font-bold ${CONF_STYLE[plan.confidence]}`}>Tingkat keyakinan: {plan.confidence}</span>}
        </div>
        <p className="mt-3 flex items-start gap-1.5 text-xs text-ink-muted"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Fokus 7 hari ini bukan membangun bisnis sempurna. Tujuannya mendapatkan bukti nyata bahwa ide ini layak dilanjutkan.</p>

        {/* Progress */}
        <div className="mt-5 rounded-2xl border border-borderc bg-white p-4" data-testid="launch-progress">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-ink">Progres Anda</span>
            <span className="text-sm font-bold text-brand" data-testid="launch-progress-count">{doneCount} dari 7 hari selesai</span>
          </div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${(doneCount / 7) * 100}%` }} />
          </div>
        </div>

        {/* Day cards */}
        <div className="mt-6 space-y-4">
          {days.map((d, i) => <DayCard key={i} d={d} done={completedSet.has(d.day)} onToggle={markDay} />)}
        </div>

        {/* Decision guide */}
        {(dg.dimensions?.length || dg.options?.length) ? (
          <section className="mt-8 rounded-2xl border-2 border-brand-accent bg-brand/5 p-5 sm:p-6" data-testid="decision-guide">
            <div className="flex items-center gap-1.5 text-brand-accent"><Scale className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-wide">Hari 7 — Cara Memutuskan</span></div>
            <h2 className="mt-1 font-heading text-lg font-semibold text-ink">Evaluasi bukti Anda, lalu putuskan</h2>

            {dg.dimensions?.length ? (
              <div className="mt-4 space-y-2">
                {dg.dimensions.map((dim, i) => (
                  <div key={i} className="rounded-xl border border-borderc bg-white p-3" data-testid={`decision-dim-${i}`}>
                    <div className="text-xs font-bold text-ink">{dim.name}</div>
                    <p className="mt-0.5 text-sm text-ink-soft leading-snug">{dim.question}</p>
                  </div>
                ))}
              </div>
            ) : null}

            {dg.options?.length ? (
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                {dg.options.map((o, i) => (
                  <div key={i} className={`rounded-xl border-2 p-4 ${DECISION_STYLE[o.label] || "border-borderc"}`} data-testid={`decision-option-${i}`}>
                    <div className="flex items-center gap-1.5"><Flag className="h-4 w-4" /><span className="text-sm font-extrabold">{o.label}</span></div>
                    <p className="mt-1.5 text-sm text-ink-soft leading-snug">{o.meaning}</p>
                  </div>
                ))}
              </div>
            ) : null}

            {dg.pivot_note && (
              <div className="mt-4 flex items-start gap-2 rounded-xl border border-brand-accent/40 bg-white p-3" data-testid="pivot-note">
                <GitBranch className="mt-0.5 h-4 w-4 shrink-0 text-brand-accent" />
                <p className="text-sm text-ink-soft leading-relaxed">{dg.pivot_note}</p>
              </div>
            )}
          </section>
        ) : null}

        {/* Closing */}
        <div className="mt-8 rounded-2xl border border-borderc bg-white p-5 text-center">
          <p className="text-sm text-ink-soft leading-relaxed">Kerjakan satu hari dalam satu waktu. Setiap langkah dirancang untuk menjaga modal Anda sambil mengumpulkan bukti nyata.</p>
          <Button onClick={() => navigate("/dashboard")} data-testid="launch-to-dashboard" variant="outline" className="mt-4 h-11 gap-2 border-borderc px-6"><ArrowLeft className="h-4 w-4" /> Kembali ke Dashboard</Button>
        </div>
      </div>
    </DashboardLayout>
  );
}

function DayCard({ d, done, onToggle }) {
  return (
    <section className={`rounded-2xl border bg-white p-5 sm:p-6 transition-colors ${done ? "border-brand bg-brand/5" : "border-borderc"}`} data-testid={`day-card-${d.day}`}>
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl font-heading text-base font-extrabold ${done ? "bg-brand text-white" : "bg-brand text-white"}`}>{d.day}</div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold uppercase tracking-wide text-brand-accent">Hari {d.day}</div>
          <h3 className="font-heading text-base font-bold text-ink leading-tight" data-testid={`day-title-${d.day}`}>{d.title}</h3>
        </div>
        <button onClick={() => onToggle(d.day, !done)} data-testid={`day-toggle-${d.day}`}
          className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
            done ? "border-brand bg-brand text-white" : "border-borderc bg-white text-ink-muted hover:border-brand hover:text-brand"}`}>
          {done ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
          {done ? "Selesai" : "Tandai selesai"}
        </button>
      </div>

      <div className="mt-4 space-y-3">
        <Block icon={Target} label="Tujuan hari ini">{d.goal}</Block>

        {d.actions?.length ? (
          <div>
            <div className="flex items-center gap-1.5 text-xs font-semibold text-ink-muted"><ListChecks className="h-3.5 w-3.5" /> Yang Anda lakukan</div>
            <ul className="mt-1.5 list-disc pl-5 text-sm text-ink space-y-1 leading-relaxed">{d.actions.map((a, i) => <li key={i}>{a}</li>)}</ul>
          </div>
        ) : null}

        <Block icon={Flag} label="Output hari ini">{d.output}</Block>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
          <div className="rounded-xl border border-brand-accent/40 bg-brand/5 p-3" data-testid={`day-budget-${d.day}`}>
            <div className="flex items-center gap-1.5 text-xs font-bold text-brand"><Wallet className="h-3.5 w-3.5" /> {d.day === 7 ? "Budget tambahan" : "Budget maksimum"}</div>
            <div className="mt-1 font-heading text-base font-extrabold text-ink">{d.day === 7 ? "Rp0" : (d.budget || "Rp0")}</div>
            {d.day === 7 && <p className="mt-1.5 text-xs text-ink-soft leading-snug">Gunakan data dan hasil uji dari Hari 1–6. Tidak perlu mengeluarkan modal baru hanya untuk mengambil keputusan.</p>}
          </div>
          <div className="rounded-xl border border-borderc bg-surface p-3" data-testid={`day-success-${d.day}`}>
            <div className="flex items-center gap-1.5 text-xs font-bold text-ink-muted"><CheckCircle2 className="h-3.5 w-3.5 text-brand" /> Tanda berhasil</div>
            <p className="mt-1 text-sm text-ink-soft leading-snug">{d.success_signal}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Block({ icon: Icon, label, children }) {
  if (!children) return null;
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs font-semibold text-ink-muted"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <p className="mt-0.5 text-sm text-ink leading-relaxed">{children}</p>
    </div>
  );
}
